import React from 'react';

import { FlagCatalan } from '../components/icons/flagCatalan';
import { FlagFrance } from '../components/icons/flagFrance';
import { FlagGermany } from '../components/icons/flagGermany';
import { FlagItaly } from '../components/icons/flagItaly';
import { FlagNetherlands } from '../components/icons/flagNetherlands';
import { FlagSpain } from '../components/icons/flagSpain';
import { FlagUk } from '../components/icons/flagUk';
import type { IconProps } from '../components/icons/icon-types';

export enum Language {
  EN_BE = 'en-be',
  FR_BE = 'fr-be',
  NL_BE = 'nl-be',

  DE_DE = 'de-de',
  EN_DE = 'en-de',

  EN_ES = 'en-es',
  ES_ES = 'es-es',
  CA_ES = 'ca-es',

  EN_GB = 'en-gb',

  EN_IE = 'en-ie',

  EN_IT = 'en-it',
  IT_IT = 'it-it',

  EN_NL = 'en-nl',
  NL_NL = 'nl-nl',

  FR_FR = 'fr-fr',
  EN_FR = 'en-fr',

  FR_CH = 'fr-ch',
  EN_CH = 'en-ch',
}

export enum LanguageIsoCode {
  EN = 'en',
  CA = 'ca',
  DE = 'de',
  ES = 'es',
  FR = 'fr',
  IT = 'it',
  NL = 'nl',
}

export enum BackendLanguageCode {
  FR_BE = 'fr-be',
  DE_DE = 'de-de',
  IT_IT = 'it-it',
  EN_GB = 'en-gb',
  ES_ES = 'es-es',
  FR_FR = 'fr-fr',
  CA = 'ca',
  NL_BE = 'nl-be',
}

export const getIsoLanguageCodeFromLanguage = (language: Language) =>
  language.substring(0, 2) as LanguageIsoCode;

export const getDisplayTextForLanguage = (language: Language) => {
  switch (language) {
    case Language.EN_BE:
    case Language.EN_DE:
    case Language.EN_ES:
    case Language.EN_GB:
    case Language.EN_IE:
    case Language.EN_IT:
    case Language.EN_NL:
    case Language.EN_FR:
    case Language.EN_CH:
      return 'EN';
    case Language.FR_BE:
    case Language.FR_FR:
    case Language.FR_CH:
      return 'FR';
    case Language.NL_BE:
    case Language.NL_NL:
      return 'NL';
    case Language.DE_DE:
      return 'DE';
    case Language.IT_IT:
      return 'IT';
    case Language.ES_ES:
      return 'ES';
    case Language.CA_ES:
      return 'CA';
    default:
      throw new Error(`No display text found for Language ${language as string}`);
  }
};

export const getFlagIconForLanguage = (language: Language, iconProps: IconProps = {}) => {
  switch (language) {
    case Language.EN_BE:
    case Language.EN_DE:
    case Language.EN_ES:
    case Language.EN_GB:
    case Language.EN_IE:
    case Language.EN_IT:
    case Language.EN_NL:
    case Language.EN_FR:
    case Language.EN_CH:
      return <FlagUk {...iconProps} />;
    case Language.FR_BE:
    case Language.FR_FR:
    case Language.FR_CH:
      return <FlagFrance {...iconProps} />;
    case Language.NL_BE:
    case Language.NL_NL:
      return <FlagNetherlands {...iconProps} />;
    case Language.DE_DE:
      return <FlagGermany {...iconProps} />;
    case Language.IT_IT:
      return <FlagItaly {...iconProps} />;
    case Language.ES_ES:
      return <FlagSpain {...iconProps} />;
    case Language.CA_ES:
      return <FlagCatalan {...iconProps} />;
    default:
      throw new Error(`No flag icon found for Language ${language as string}`);
  }
};

export const getBackendLanguage = (language: Language): BackendLanguageCode => {
  switch (language) {
    case Language.EN_BE:
    case Language.EN_DE:
    case Language.EN_ES:
    case Language.EN_GB:
    case Language.EN_IE:
    case Language.EN_IT:
    case Language.EN_NL:
    case Language.EN_FR:
    case Language.EN_CH:
      return BackendLanguageCode.EN_GB;
    case Language.FR_BE:
    case Language.FR_FR:
    case Language.FR_CH:
      return BackendLanguageCode.FR_BE;
    case Language.NL_BE:
    case Language.NL_NL:
      return BackendLanguageCode.NL_BE;
    case Language.DE_DE:
      return BackendLanguageCode.DE_DE;
    case Language.IT_IT:
      return BackendLanguageCode.IT_IT;
    case Language.ES_ES:
      return BackendLanguageCode.ES_ES;
    case Language.CA_ES:
      return BackendLanguageCode.CA;
    default:
      throw new Error(`No backend language found for Language ${language as string}`);
  }
};

/**
 * See Adyen's documentation for supported locales:
 * https://docs.adyen.com/online-payments/build-your-integration/sessions-flow/?platform=Web&integration=Drop-in&version=6.19.0#configure
 * https://github.com/Adyen/adyen-web/tree/main/packages/server/translations
 */
export const getAdyenLocale = (language: Language) => {
  switch (language) {
    case Language.EN_BE:
    case Language.EN_DE:
    case Language.EN_ES:
    case Language.EN_GB:
    case Language.EN_IE:
    case Language.EN_IT:
    case Language.EN_NL:
    case Language.EN_FR:
    case Language.EN_CH:
      return 'en-US';
    case Language.FR_BE:
    case Language.FR_FR:
    case Language.FR_CH:
      return 'fr-FR';
    case Language.NL_BE:
    case Language.NL_NL:
      return 'nl-NL';
    case Language.DE_DE:
      return 'de-DE';
    case Language.IT_IT:
      return 'it-IT';
    case Language.ES_ES:
      return 'es-ES';
    case Language.CA_ES:
      return 'ca-ES';
    default:
      throw new Error(`No Adyen locale found for Language ${language as string}`);
  }
};

export const getUrlValueForLanguage = (language: Language) => {
  switch (language) {
    case Language.EN_BE:
    case Language.EN_DE:
    case Language.EN_ES:
    case Language.EN_GB:
    case Language.EN_IE:
    case Language.EN_IT:
    case Language.EN_NL:
    case Language.EN_FR:
    case Language.EN_CH:
      return 'en';
    default:
      return language.toString();
  }
};
